import type {DiffAnchor} from '@github-ui/diffs/types'
import type {PropsWithChildren} from 'react'
import {createContext, useContext, useMemo} from 'react'

import type {HunkData} from '../helpers/hunk-data-helpers'
import type {ThreadsData} from '../types'
import type {ThreadSummary} from '@github-ui/conversations'
import {threadSummary} from '../helpers/conversation-helpers'
import {useDiffContext} from './DiffContext'

export type DiffLineContextData = {
  currentHunk?: HunkData
  fileAnchor: DiffAnchor
  fileLineCount: number
  filePath: string
  hasHiddenUnicodeCharacters?: boolean
  isLeftSide?: boolean
  isSplit: boolean
  modifiedLineThreads?: ThreadSummary[]
  modifiedLineHasThreads?: boolean
  nextHunk?: HunkData
  originalLineThreads?: ThreadSummary[]
  originalLineHasThreads?: boolean
  previousHunk?: HunkData
  rowId: string
}

export type DifflineContextInitOpts = Omit<DiffLineContextData, 'originalLineThreads' | 'modifiedLineThreads'> & {
  originalLineThreads?: ThreadsData | undefined
  modifiedLineThreads?: ThreadsData | undefined
}

const DiffLineContext = createContext<DiffLineContextData | null>(null)

export function DiffLineContextProvider({
  children,
  currentHunk,
  fileAnchor,
  fileLineCount,
  filePath,
  hasHiddenUnicodeCharacters,
  isLeftSide,
  isSplit,
  modifiedLineThreads,
  nextHunk,
  originalLineThreads,
  previousHunk,
  rowId,
}: PropsWithChildren<DifflineContextInitOpts>) {
  const {ghostUser} = useDiffContext()
  const memoizedModifiedLineThreadSummaries = useMemo(
    () => threadSummary(modifiedLineThreads, ghostUser),
    [ghostUser, modifiedLineThreads],
  )
  const memoizedOriginalLineThreadSummaries = useMemo(
    () => threadSummary(originalLineThreads, ghostUser),
    [ghostUser, originalLineThreads],
  )

  const contextData = useMemo(
    () => ({
      currentHunk,
      fileAnchor,
      fileLineCount,
      filePath,
      hasHiddenUnicodeCharacters,
      isLeftSide,
      isSplit,
      modifiedLineThreads: memoizedModifiedLineThreadSummaries,
      modifiedLineHasThreads: memoizedModifiedLineThreadSummaries && memoizedModifiedLineThreadSummaries.length > 0,
      nextHunk,
      originalLineThreads: memoizedOriginalLineThreadSummaries,
      originalLineHasThreads: memoizedOriginalLineThreadSummaries && memoizedOriginalLineThreadSummaries.length > 0,
      previousHunk,
      rowId,
    }),
    [
      currentHunk,
      fileAnchor,
      fileLineCount,
      filePath,
      hasHiddenUnicodeCharacters,
      isLeftSide,
      isSplit,
      memoizedModifiedLineThreadSummaries,
      memoizedOriginalLineThreadSummaries,
      nextHunk,
      previousHunk,
      rowId,
    ],
  )

  return <DiffLineContext.Provider value={contextData}>{children}</DiffLineContext.Provider>
}

export function useDiffLineContext(): DiffLineContextData {
  const contextData = useContext(DiffLineContext)
  if (!contextData) {
    throw new Error('useDiffLineContext must be used within a DiffLineContextProvider')
  }

  return contextData
}

try{ DiffLineContext.displayName ||= 'DiffLineContext' } catch {}
try{ DiffLineContextProvider.displayName ||= 'DiffLineContextProvider' } catch {}